import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Highlighter, Underline, Strikethrough, ExternalLink, StickyNote, Clock, Pencil, Check, X, Plus,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, getToken, API } from "@/lib/api";
import { HIGHLIGHT_COLORS, DEFAULT_COVER } from "@/lib/constants";

const TYPE_ICON = { highlight: Highlighter, underline: Underline, strikethrough: Strikethrough };

function coverUrl(book) {
  return book?.cover_path ? `${API}/books/${book.id}/cover-img?auth=${getToken()}` : DEFAULT_COVER;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function AnnotationCard({ a, book, onOpenBook, editing, setEditing, onSaved }) {
  const Icon = TYPE_ICON[a.type] || Highlighter;
  const isEditing = editing?.id === a.id;

  const save = async () => {
    await api.put(`/annotations/${a.id}`, { note: editing.note, color: editing.color });
    setEditing(null);
    onSaved();
    toast.success("Annotation updated");
  };

  return (
    <div data-testid="ann-item" className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-indigo-300 transition-colors">
      <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
        <span className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" style={{ color: a.color }} /> Page {a.page}</span>
        {!isEditing && (
          <button data-testid="ann-edit" onClick={() => setEditing({ id: a.id, note: a.note || "", color: a.color })}
            className="flex items-center gap-1 hover:text-indigo-600"><Pencil className="h-3 w-3" /> Edit</button>
        )}
      </div>
      <p className="text-sm text-slate-800 dark:text-slate-200 border-l-4 pl-2 cursor-pointer" style={{ borderColor: a.color }}
        onClick={() => book && !isEditing && onOpenBook(book, a.page)}>
        {a.text || <span className="italic text-slate-400">(no text captured)</span>}
      </p>
      {isEditing ? (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-1.5">
            {HIGHLIGHT_COLORS.map((c) => (
              <button key={c.hex} onClick={() => setEditing({ ...editing, color: c.hex })}
                className={`h-5 w-5 rounded-full border-2 ${editing.color === c.hex ? "border-slate-900 dark:border-white" : "border-transparent"}`}
                style={{ backgroundColor: c.hex }} />
            ))}
            <input type="color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="h-5 w-5 rounded cursor-pointer" />
          </div>
          <Textarea data-testid="ann-edit-note" value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })}
            placeholder="Add a note…" className="text-sm h-16 resize-none" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="ann-edit-save" size="sm" onClick={save}><Check className="h-3.5 w-3.5 mr-1" />Save</Button>
          </div>
        </div>
      ) : (
        <>
          {a.note && <p className="mt-2 text-xs text-slate-500 flex items-start gap-1.5"><StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {a.note}</p>}
          {a.created_at && <p className="mt-1.5 text-[11px] text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDate(a.created_at)}</p>}
        </>
      )}
    </div>
  );
}

function BookColumn({ book, onRemove, onOpenBook, editing, setEditing }) {
  const [anns, setAnns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/annotations?book_id=${book.id}`).then((r) => setAnns(r.data)).finally(() => setLoading(false));
  }, [book.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="ann-column" className="w-80 shrink-0 flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 overflow-hidden">
      <div className="flex items-center gap-2 p-2.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <img src={coverUrl(book)} alt="" className="h-10 w-7 object-cover rounded shrink-0"
          onError={(e) => { e.currentTarget.src = DEFAULT_COVER; }} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate text-slate-900 dark:text-slate-100">{book.title}</p>
          <p className="text-xs text-slate-500 truncate">{anns.length} annotation{anns.length === 1 ? "" : "s"}</p>
        </div>
        <button data-testid="ann-column-open" onClick={() => onOpenBook(book, 1)} className="p-1 text-slate-400 hover:text-indigo-600" title="Open book">
          <ExternalLink className="h-4 w-4" />
        </button>
        <button data-testid="ann-column-remove" onClick={() => onRemove(book.id)} className="p-1 text-slate-400 hover:text-red-500" title="Remove column">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto thin-scrollbar p-2 space-y-2">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
        ) : anns.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No annotations yet.</p>
        ) : (
          anns.map((a) => (
            <AnnotationCard key={a.id} a={a} book={book} onOpenBook={onOpenBook}
              editing={editing} setEditing={setEditing} onSaved={load} />
          ))
        )}
      </div>
    </div>
  );
}

export default function AnnotationsView({ books, onOpenBook }) {
  const [columnIds, setColumnIds] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (books.length && columnIds.length === 0) setColumnIds([books[0].id]);
  }, [books]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = columnIds.map((id) => books.find((b) => b.id === id)).filter(Boolean);
  const available = books.filter((b) => !columnIds.includes(b.id));

  const addColumn = (id) => { if (id && !columnIds.includes(id)) setColumnIds([...columnIds, id]); };
  const removeColumn = (id) => setColumnIds(columnIds.filter((c) => c !== id));

  if (books.length === 0) {
    return <p className="text-sm text-slate-400 py-10 text-center">No documents in your library yet.</p>;
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <p className="text-sm text-slate-500 mb-3">
        View annotations from multiple documents side by side. Add a column for each book; edit notes inline or open a book in the reader.
      </p>
      <div className="flex-1 min-h-0 flex gap-4 overflow-x-auto thin-scrollbar pb-2">
        {columns.map((book) => (
          <BookColumn key={book.id} book={book} onRemove={removeColumn} onOpenBook={onOpenBook}
            editing={editing} setEditing={setEditing} />
        ))}
        <div className="w-72 shrink-0 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800 p-4 gap-3">
          <Plus className="h-6 w-6 text-slate-300" />
          <p className="text-sm text-slate-400 text-center">Add a document column</p>
          <Select value="" onValueChange={addColumn} disabled={available.length === 0}>
            <SelectTrigger data-testid="ann-add-column" className="w-full">
              <SelectValue placeholder={available.length ? "Choose a document…" : "All documents added"} />
            </SelectTrigger>
            <SelectContent>
              {available.map((b) => <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
